import React, { useEffect, useCallback, useState } from "react";
import { Math as CesiumMath, Ion, Cartographic, Viewer, Rectangle, Cartesian3 } from "cesium";
import { Container } from "@chakra-ui/react";
import { getFromCache, mergeWithFallback, removeToCache, rightClickOnPointClouds, showOrHideTimeLine, showToast } from "../utilies/Utility";
import CesiumNavigation from "cesium-navigation-es6";
import { enableAltHoverVertexPreview, handleHighlightMultiGeojson, setupPointClickLogger } from "../utilies/GeojsonUtility";
import SplashScreen from "./SplashScreen";
import { setImageryByName } from "../utilies/ImageryUtility";
import Type from "../utilies/Types";
import { setupOSMLaneletClickHandler, setupOSMLineClickHandler } from "../utilies/OsmUtility";
import { setupCesiumRightClickOverlays } from "../utilies/RightClickUtility";
import { addGrid } from "../utilies/GridUtility";
import { serviceAuthUsers, serviceGetUsers } from "../api/services/user";
import QueryTable from "./header/right/QueryTable";
import { serviceGetDatabases } from "../api/services/database";
import { serviceGetCity } from "../api/services/city";
import MapInfo from "./MapInfo";
import { serviceGetReviewTitles } from "../api/services/comment";
import CameraInfo from "./CameraInfo";
import { serviceGetTask } from "../api/services/task";
import { initKeyboardPanControls, installGlobalSpaceCameraUnlock, installGlobalSpaceDoublePressModeToggle, setSceneMode } from "../utilies/MapUtility";
import { serviceGetImagary, serviceGetOrUpdateSettings } from "../api/services/settings";
import { setupEntityClickHighlight } from "../utilies/other/EntityHighlight";
import { installEmptyEntityClick, resetHIBHighlights } from "../utilies/GlobalToolsUtility";
import { setupHoverCursor } from "../utilies/other/Cursor";
import Loading from "./Loading";
import { initShiftClickSelect } from "../utilies/other/GlobalMultipleSelection";
import { createBackgroundChecker } from "../background/createBackgroundChecker";
import { getProjects } from "../utilies/services/project/GetProject";
import HeaderLeft from "./header/left";
import HeaderRight from "./header/right";
import SideBarRight from "./side/right";
import SideBarLeft from "./side/left";
import { useAppStore } from "../context/store";
import { useMapObjects } from "../context/MapObjectsContext";
import { CustomInfobox } from "./InfoTable";

Ion.defaultAccessToken = process.env.REACT_APP_CESIUM_KEY;

// ---- Cesium safety: zoom clamp + crash logger ----
const isFiniteCartesian3 = (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);

function installCesiumRenderCrashLogger(viewer) {
  if (!viewer || viewer.__renderCrashLoggerInstalled) return;
  viewer.__renderCrashLoggerInstalled = true;

  window.__cesiumRenderCrashCount = 0;

  viewer.scene.renderError.addEventListener((scene, error) => {
    window.__cesiumRenderCrashCount++;
    console.error("[CESIUM renderError]", error);
  });
}

function installMinCameraHeightClamp(viewer, minHeightMeters = 30) {
  if (!viewer || viewer.__minHeightClampInstalled) return;
  viewer.__minHeightClampInstalled = true;

  const camera = viewer.camera;

  const clamp = () => {
    try {
      const c = Cartographic.fromCartesian(camera.positionWC);
      if (!c || !Number.isFinite(c.height)) return;

      if (c.height < minHeightMeters) {
        c.height = minHeightMeters;
        const fixed = Cartesian3.fromRadians(c.longitude, c.latitude, c.height);
        if (!isFiniteCartesian3(fixed)) return;
        camera.position = fixed;
      }
    } catch {}
  };

  // clamp every frame (prevents overshoot)
  viewer.clock.onTick.addEventListener(clamp);
}

const Main = () => {
  const isLoadingApp = useAppStore((s) => s.isLoadingApp);
  const { setMapObjects, mapObjectsRef } = useMapObjects();
  const snappingOptions = useAppStore((s) => s.snappingOptions);
  const city = useAppStore((s) => s.city);
  const defaultCity = useAppStore((s) => s.defaultCity);
  const userData = useAppStore((s) => s.userData);
  const simplifyToleranceSetting = useAppStore((s) => s.simplifyToleranceSetting);
  const infoboxRef = useAppStore((s) => s.infoboxRef);

  const viewerRef = useAppStore.getState().viewerRef;
  const cesiumContainer = useAppStore.getState().cesiumContainer;
  const setIsLoadingApp = useAppStore.getState().setIsLoadingApp;
  const setEditFeatureDialog = useAppStore.getState().setEditFeatureDialog;
  const setTimeLineMode = useAppStore.getState().setTimeLineMode;
  const setGridMode = useAppStore.getState().setGridMode;
  const setGridColor = useAppStore.getState().setGridColor;
  const setGridSize = useAppStore.getState().setGridSize;
  const setDefaultGeojsonStyle = useAppStore.getState().setDefaultGeojsonStyle;
  const editModeRef = useAppStore.getState().editModeRef;
  const setBasicGeojsonStyle = useAppStore.getState().setBasicGeojsonStyle;
  const setImportUrls = useAppStore.getState().setImportUrls;
  const setConfigSpeedProfile = useAppStore.getState().setConfigSpeedProfile;
  const setDefaultOsmStyle = useAppStore.getState().setDefaultOsmStyle;

  const setCurrentLayerEditing = useAppStore.getState().setCurrentLayerEditing;
  const setIsOpenAddonsMenu = useAppStore.getState().setIsOpenAddonsMenu;
  const setEditMode = useAppStore.getState().setEditMode;
  const originalCameraControls = useAppStore.getState().originalCameraControls;
  const setEditHandler = useAppStore.getState().setEditHandler;
  const pushUndo = useAppStore.getState().pushUndo;
  const createLaneletModeRef = useAppStore.getState().createLaneletModeRef;
  const associationRef = useAppStore.getState().associationRef;
  const selectedEntitiesRef = useAppStore.getState().selectedEntitiesRef;
  const setIsOpenEditCoordianteModal = useAppStore.getState().setIsOpenEditCoordianteModal;
  const setEditCoordianteModalData = useAppStore.getState().setEditCoordianteModalData;
  const isEnableSelectingCommentRef = useAppStore.getState().isEnableSelectingCommentRef;
  const directionArrowSettingRef = useAppStore.getState().directionArrowSettingRef;
  const setDirectionArrowSetting = useAppStore.getState().setDirectionArrowSetting;
  const setProjects = useAppStore.getState().setProjects;
  const setUsers = useAppStore.getState().setUsers;
  const setDatabases = useAppStore.getState().setDatabases;
  const setCity = useAppStore.getState().setCity;
  const setDb = useAppStore.getState().setDb;
  const setDefaultCity = useAppStore.getState().setDefaultCity;
  const canOpenInfoboxRef = useAppStore.getState().canOpenInfoboxRef;
  const setShowMapInfo = useAppStore.getState().setShowMapInfo;
  const generateArcRef = useAppStore.getState().generateArcRef;
  const setMoveToAnotherLayerDialog = useAppStore.getState().setMoveToAnotherLayerDialog;
  const setReviewTitle = useAppStore.getState().setReviewTitle;
  const setTasks = useAppStore.getState().setTasks;
  const canHightlightEntityRef = useAppStore.getState().canHightlightEntityRef;
  const appModeRef = useAppStore.getState().appModeRef;
  const setSnappingOptions = useAppStore.getState().setSnappingOptions;
  const setFilterNodeMinHeight = useAppStore.getState().setFilterNodeMinHeight;
  const setFilterNodeMaxHeight = useAppStore.getState().setFilterNodeMaxHeight;
  const setSimplifyToleranceSetting = useAppStore.getState().setSimplifyToleranceSetting;
  const highlightToolsRef = useAppStore.getState().highlightToolsRef;
  const savedHotkeysRef = useAppStore.getState().savedHotkeysRef;
  const setCesiumAssetsList = useAppStore.getState().setCesiumAssetsList;
  const userCanEditingRef = useAppStore.getState().userCanEditingRef;
  const simplifyToleranceSettingRef = useAppStore.getState().simplifyToleranceSettingRef;
  const setIsLoggedIn = useAppStore.getState().setIsLoggedIn;
  const setSelectedEntities = useAppStore.getState().setSelectedEntities;
  const pushUndoAction = useAppStore.getState().pushUndoAction;
  const turningArcModeRef = useAppStore((s) => s.turningArcModeRef);

  const [droppedFiles, setDroppedFiles] = useState([]);

  useEffect(() => {
    if (!cesiumContainer.current) return;

    // Initialize Cesium Viewer
    const viewer = new Viewer(cesiumContainer.current, {
      baseLayerPicker: true,
      fullscreenButton: false,
      infoBox: true,
      imageryProvider: false,
      navigationHelpButton: false,
      homeButton: false,
      terrainProviderViewModels: [],
      shouldAnimate: true,
      contextOptions: {
        webgl: { alpha: true },
        webgl2: { alpha: true },
      }, 
    });

    const MIN_HEIGHT_M = 3; // adjust if needed
    viewer.scene.screenSpaceCameraController.minimumZoomDistance = MIN_HEIGHT_M;
    viewer.scene.screenSpaceCameraController.maximumZoomDistance = Number.MAX_VALUE;

    new CesiumNavigation(viewer, {
      defaultResetView: new Cartographic(CesiumMath.toRadians(-112.074), CesiumMath.toRadians(33.4484), 10000),
      enableZoomControls: true,
      enableCompass: true,
      resetTooltip: "Reset View",
      zoomInTooltip: "Zoom In",
      zoomOutTooltip: "Zoom Out",
    });

    viewer.infoBox.frame.addEventListener("load", () => {
      const doc = viewer.infoBox.frame.contentDocument || viewer.infoBox.frame.contentWindow.document;
      const style = doc.createElement("style");
      style.innerHTML = `
        .cesium-infoBox-defaultTable th {
          font-weight: normal;
          padding: 3px;
          vertical-align: middle;
          text-align: left !important;
        }
      `;
      doc.head.appendChild(style);
    });

    setSceneMode(viewer, Type.SCENE.SCENE_2D);
    viewerRef.current = viewer;

installCesiumRenderCrashLogger(viewer);
installMinCameraHeightClamp(viewer, MIN_HEIGHT_M);


    initializeGlobalEvents(viewer);
    getDataFromCacheAndConfigs(viewer);
    initGetDataFromServer(viewer);

    return () => {
      viewerRef.current?.destroy();
      viewerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (city && defaultCity && viewerRef.current) {
      const selectedCity = city.find((c) => c.id === defaultCity || c.name.toLowerCase() === defaultCity);

      // Helper: create a valid Cesium rectangle from possibly unordered coords
      const toSafeRectangle = (west, south, east, north) => {
        // swap if reversed (keeps logical area but fixes order)
        if (east < west) [west, east] = [east, west]; // ensure east > west
        if (north < south) [south, north] = [north, south]; // ensure north > south

        // nudge if zero width/height to prevent degenerate frustum
        const EPS = 1e-8; // tiny epsilon in degrees
        if (Math.abs(east - west) < EPS) east = west + EPS;
        if (Math.abs(north - south) < EPS) north = south + EPS;

        return Rectangle.fromDegrees(west, south, east, north);
      };

      if (selectedCity?.bounding_box?.length) {
        // NOTE: keeping your original data order assumption (p[0]=lat, p[1]=lon)
        const lats = selectedCity.bounding_box.map((p) => p[0]); // lat
        const lons = selectedCity.bounding_box.map((p) => p[1]); // lon

        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);

        // Build a safe rectangle regardless of input ordering
        const rectangle = toSafeRectangle(minLon, minLat, maxLon, maxLat);

        // Fly camera to the rectangle
        viewerRef.current.camera.flyTo({
          destination: rectangle,
          duration: 2.0,
        });
      } else if (selectedCity?.the_geom?.length === 2) {
        const [lat, lon] = selectedCity.the_geom; // keeping your original order
        viewerRef.current.camera.flyTo({
          destination: Cartesian3.fromDegrees(lon, lat, 10000),
          duration: 2.0,
        });
      }
    }
  }, [defaultCity, city]);

  const fetchSettings = async () => {
    try {
      const res = await serviceGetOrUpdateSettings(null, userData?.token);
      const data = res?.data?.settings_data;
      if (!data) return;

      // Snap settings
      if (data.midpoint != null && data.vertex != null) {
        setSnappingOptions({
          midpoint: data.midpoint,
          vertex: data.vertex,
        });
      }

      // Direction arrow
      if (data.color != null && data.scale != null) {
        setDirectionArrowSetting({
          color: data.color,
          scale: data.scale,
        });
      }

      // General settings
      if (data.database != null) setDb(data.database);
      if (data.city != null) setDefaultCity(data.city);
      if (data.speed_profile != null) setConfigSpeedProfile(data.speed_profile);

      if (data.default_geojson_style != null) setDefaultGeojsonStyle(data.default_geojson_style);
      if (data.basic_geojson_style != null) setBasicGeojsonStyle(data.basic_geojson_style);
      if (data.default_osm_style != null) setDefaultOsmStyle(data.default_osm_style);

      if (data.min_height != null) setFilterNodeMinHeight(data.min_height);
      if (data.max_height != null) setFilterNodeMaxHeight(data.max_height);

      if (data.simplify_tolerance != null) {
        if (!data.simplify_tolerance.generate_distance) {
          data.simplify_tolerance.generate_distance = simplifyToleranceSetting.generate_distance;
        }
        if (!data.simplify_tolerance.rearrange) {
          data.simplify_tolerance.rearrange = simplifyToleranceSetting.rearrange;
        }
        if (!data.simplify_tolerance.find_break_points) {
          data.simplify_tolerance.find_break_points = simplifyToleranceSetting.find_break_points;
        }
        if (!data.simplify_tolerance.find_close_point) {
          data.simplify_tolerance.find_close_point = simplifyToleranceSetting.find_close_point;
        }

        setSimplifyToleranceSetting(data.simplify_tolerance);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  const initializeGlobalEvents = (viewer) => {
    // Calling Background Services
    createBackgroundChecker({
      timeout: 2,
      callback: () => {
        const formData = new FormData();

        formData.append("username", userData?.user?.username);

        serviceAuthUsers(formData, userData.token)
          .then((res) => {
            if (!res.data?.is_active) {
              setIsLoggedIn("false");
              removeToCache(Type.STORAGE.USERINFO);
            }
          })
          .catch((err) => {});
      },
    }).start();

    const highlightTools = setupEntityClickHighlight({ viewer, canHightlightEntityRef });
    highlightToolsRef.current = highlightTools.clearHighlights;

    setupHoverCursor(viewer);

    setupPointClickLogger(viewer);
    handleHighlightMultiGeojson(viewer);
    installEmptyEntityClick(viewer, () => {
      resetHIBHighlights(viewer);
    });

    initKeyboardPanControls({ viewer, appModeRef, savedHotkeysRef, turningArcModeRef });

    setupCesiumRightClickOverlays(viewer, {
      otherStates: {
        mapObjectsRef,
        setCurrentLayerEditing,
        setIsOpenAddonsMenu,
        setEditMode,
        originalCameraControls,
        setEditHandler,
        pushUndo,
        selectedEntitiesRef,
        snappingOptions,
        editModeRef,
        setEditFeatureDialog,
        setMapObjects,
        directionArrowSettingRef,
        setMoveToAnotherLayerDialog,
        userCanEditingRef,
        simplifyToleranceSettingRef,
        pushUndoAction,
      },
      editModeRef: editModeRef,
      resetHighlights: highlightToolsRef.current,
    });

    // For select multiple entities
    initShiftClickSelect(viewer, selectedEntitiesRef, editModeRef, setSelectedEntities);

    setupOSMLineClickHandler(viewer);
    setupOSMLaneletClickHandler(viewer);

    rightClickOnPointClouds(viewer);

    installGlobalSpaceCameraUnlock(viewer);

    installGlobalSpaceDoublePressModeToggle(useAppStore, {
      doublePressMs: 600,
    });

    enableAltHoverVertexPreview(viewer, {
      onPointContextClick: ({ entity, parentId, index, position, screenPosition }) => {
        setIsOpenEditCoordianteModal(true);
        setEditCoordianteModalData({ entity, parentId, index, position, screenPosition });
      },
    });
  };

  const getDataFromCacheAndConfigs = (viewer) => {
    fetch("/configs/config_hd_map.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load JSON");
        return res.json();
      })
      .then((json) => {
        const bgs = getFromCache(Type.STORAGE.BASIC_GEOJSON_STYLE);
        if (bgs) {
          setBasicGeojsonStyle(mergeWithFallback(json, bgs));
        } else {
          setBasicGeojsonStyle(json);
        }
      })
      .catch(() => {
        const bgs = getFromCache(Type.STORAGE.BASIC_GEOJSON_STYLE);
        if (bgs) {
          setBasicGeojsonStyle(bgs);
        }
      });

    fetch("/configs/config_speed_profile.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load JSON");
        return res.json();
      })
      .then((json) => {
        const cached = getFromCache("setting_speed_limit");
        if (cached) {
          setConfigSpeedProfile(mergeWithFallback(json, cached));
        } else {
          setConfigSpeedProfile(json);
        }
      })
      .catch(() => {
        const cached = getFromCache("setting_speed_limit");
        if (cached) {
          setConfigSpeedProfile(cached);
        }
      });

    fetch("/configs/config_osm.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load JSON");
        return res.json();
      })
      .then((json) => {
        const cached = getFromCache(Type.STORAGE.DEFAULT_OSM_STYLE);
        if (cached) {
          setDefaultOsmStyle(mergeWithFallback(json, cached));
        } else {
          setDefaultOsmStyle(json);
        }
      })
      .catch(() => {
        const cached = getFromCache(Type.STORAGE.DEFAULT_OSM_STYLE);
        if (cached) {
          setDefaultOsmStyle(cached);
        }
      });

    const mapStyle = getFromCache("mapStyle");
    if (mapStyle && mapStyle != "GeoTiff Tile") {
      const match = mapStyle.match(/#([0-9a-f]{3,8})/i);

      if (match) {
        setImageryByName(viewer, "Color", match[0]);
      } else {
        setImageryByName(viewer, mapStyle);
      }
    }

    const settingArrow = getFromCache("setting_arrow");
    if (settingArrow) {
      setDirectionArrowSetting(settingArrow);
    }

    const settingSimplifyTolerance = getFromCache("setting_simplify_tolerance");
    if (settingSimplifyTolerance) {
      if (!settingSimplifyTolerance.generate_distance) {
        settingSimplifyTolerance.generate_distance = simplifyToleranceSetting.generate_distance;
      }
      if (!settingSimplifyTolerance.rearrange) {
        settingSimplifyTolerance.rearrange = simplifyToleranceSetting.rearrange;
      }
      if (!settingSimplifyTolerance.find_break_points) {
        settingSimplifyTolerance.find_break_points = simplifyToleranceSetting.find_break_points;
      }
      if (!settingSimplifyTolerance.find_close_point) {
        settingSimplifyTolerance.find_close_point = simplifyToleranceSetting.find_close_point;
      }
      setSimplifyToleranceSetting(settingSimplifyTolerance);
    }

    const settingMapInfo = getFromCache("setting_mapinfo");
    if (!settingMapInfo) {
      setShowMapInfo(false);
    } else {
      setShowMapInfo(true);
    }

    const settingDb = getFromCache("setting_db");
    if (settingDb) {
      setDb(settingDb);
    }

    const settingDefaultCity = getFromCache("setting_city");
    if (settingDefaultCity) {
      setDefaultCity(settingDefaultCity);
    }

    const iu = getFromCache(Type.STORAGE.IMPORT_URLS);
    if (iu) {
      setImportUrls(iu);
    }

    const timeline = getFromCache(Type.STORAGE.STATE_TIMELINE);
    if (timeline != null) {
      showOrHideTimeLine(viewerRef.current, timeline);
      setTimeLineMode(timeline);
    }

    const grid = getFromCache(Type.STORAGE.STATE_GRID);
    if (grid != null) {
      setGridMode(grid.visibility);
      setGridColor(grid.color);
      setGridSize([Number(grid.size)]);
      if (grid.visibility) {
        setTimeout(() => {
          addGrid(viewerRef.current, Number(grid.size), grid.color);
        }, 1000);
      }
    }

    const defaultStyle = getFromCache(Type.STORAGE.DEFAULT_GEOJSON_STYLE);
    if (defaultStyle != null) {
      setDefaultGeojsonStyle(defaultStyle);
    }
  };

  const initGetDataFromServer = () => {
    // Get projects from server
    getProjects({
      userData,
      setProjects,
    });

    // Get tasks from server
    let serviceData = {};
    serviceData.params = JSON.stringify({});
    serviceGetTask(serviceData, userData.token)
      .then((res) => {
        if (res.data.status === "success") {
          setTasks(res.data.tasks);
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });

    // Get database from server
    const formData = new FormData();
    formData.append("username", userData.user.username);
    serviceGetDatabases(formData)
      .then((res) => {
        if (res.data.status == "success") {
          setDatabases(res.data.schemas);
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.WARNING);
      });

    // Get cities from server
    serviceGetCity()
      .then((res) => {
        setCity(res.data.results);
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.WARNING);
      });

    // Get users from server
    serviceGetUsers()
      .then((res) => {
        const usersObj = res.data;
        const usersArray = Object.entries(usersObj)
          .map(([id, u]) => ({ id, ...u }))
          .sort((a, b) => {
            const cmpFirst = (a.first_name || "").localeCompare(b.first_name || "", undefined, { sensitivity: "base" });
            if (cmpFirst !== 0) return cmpFirst;
            return (a.last_name || "").localeCompare(b.last_name || "", undefined, { sensitivity: "base" });
          });

        const sortedObj = Object.fromEntries(
          usersArray.map((u) => [
            u.id,
            {
              username: u.username,
              roles: u.roles,
              first_name: u.first_name,
              last_name: u.last_name,
              email: u.email,
              is_active: u.is_active,
              is_staff: u.is_staff,
              is_superuser: u.is_superuser,
            },
          ]),
        );

        setUsers(sortedObj);
      })
      .catch((err) => {
        console.error("Error", err);
      });

    // Get Imagary from server
    serviceGetImagary(null, userData.token)
      .then((res) => {
        if (res?.data?.assets) {
          setCesiumAssetsList(res?.data?.assets);
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });

    // Get review titles from server
    serviceGetReviewTitles()
      .then((res) => {
        setReviewTitle(res.data.review_types);
      })
      .catch((err) => {
        console.error("Error", err);
      });

    fetchSettings();
  };

  // Handle full load
  useEffect(() => {
    const waitForMapToLoad = () =>
      new Promise((resolve) => {
        const check = () => {
          if (viewerRef?.current?.scene?.globe.tilesLoaded) resolve();
          else setTimeout(check, 100);
        };
        check();
      });

    const load = async () => {
      await waitForMapToLoad();
      setIsLoadingApp(false);
    };

    load();
  }, []);
  // Handle drag & drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const newDroppedFiles = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newDroppedFiles.push({ name: file.name, content: event.target.result });
        if (newDroppedFiles.length === files.length) {
          setDroppedFiles((prev) => [...prev, ...newDroppedFiles]);
        }
      };
      reader.readAsText(file);
    });
  }, []);
  const handleDragOver = (e) => e.preventDefault();

  return (
    <Container fluid width="100vw" height="100vh" p={0} onDrop={handleDrop} onDragOver={handleDragOver} className="app-root">
      {isLoadingApp && <SplashScreen />}
      <Loading />

      <HeaderLeft />
      <HeaderRight />

      <SideBarLeft droppedFiles={[droppedFiles, setDroppedFiles]} />
      <SideBarRight />

      <Container fluid ref={cesiumContainer} width="100vw" height="100vh" p={0} className="cesium-print-only" />
      <MapInfo />
      <CameraInfo />

      <QueryTable />

      <CustomInfobox
        ref={infoboxRef}
        viewer={viewerRef.current}
        editModeRef={editModeRef}
        associationRef={associationRef}
        createLaneletModeRef={createLaneletModeRef}
        isEnableSelectingCommentRef={isEnableSelectingCommentRef}
        canOpenInfoboxRef={canOpenInfoboxRef}
        generateArcRef={generateArcRef}
        setMapObjects={setMapObjects}
      />
    </Container>
  );
};

export default Main;
